import { Plus, Search, X } from 'lucide-react';
import { Input, Tooltip } from '@strivelabs/ui-core/v2';
import { NEW_CHAT_SHORTCUT, useNewChat } from '../../hooks/useNewChat';

export interface ChatSearchProps {
  value: string;
  onChange: (value: string) => void;
}

/**
 * The chat list's header row: a compact search field with the new-chat action
 * beside it. The field is deliberately narrower than the panel — the list below
 * is the content, and a full-width field made the search look like the subject
 * of the tab rather than a way to narrow it.
 *
 * Geometry from the Home Shell design: a 30px field with a 12px mark and a
 * 13px value, and the new-chat action as a 24px solid disc — the one filled
 * control in the rail, which is what makes it read as the way in rather than
 * as another icon button beside the search.
 *
 * Debouncing lives in `useChatList`.
 */
export function ChatSearch({ value, onChange }: ChatSearchProps) {
  const startNewChat = useNewChat();

  return (
    <div className="flex items-center justify-end gap-1.5 px-0.5">
      <Input
        type="search"
        value={value}
        onChange={onChange}
        aria-label="Search chats"
        placeholder="Search chats"
        leftIcon={<Search size={12} />}
        className="min-w-0 flex-1"
        // Only the size differs here — hover and focus now live on the Input.
        classNames={{ field: 'h-[30px]', input: 'text-[13px] leading-[18px]' }}
        rightIcon={
          value ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => onChange('')}
              className="relative grid size-4 place-items-center rounded-v2-sm text-v2-muted-foreground transition-colors before:absolute before:-inset-1.5 before:content-[''] hover:text-v2-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-ring"
            >
              <X size={12} />
            </button>
          ) : undefined
        }
      />
      <Tooltip label="New chat" shortcut={NEW_CHAT_SHORTCUT} side="bottom">
        <button
          type="button"
          aria-label={`New chat (${NEW_CHAT_SHORTCUT})`}
          onClick={startNewChat}
          className="relative grid size-6 shrink-0 place-items-center rounded-full bg-v2-foreground text-v2-background transition-[background-color,transform] before:absolute before:-inset-2 before:content-[''] hover:bg-v2-foreground/90 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-sidebar-ring focus-visible:ring-offset-2 focus-visible:ring-offset-v2-sidebar motion-reduce:active:scale-100"
        >
          <Plus size={14} />
        </button>
      </Tooltip>
    </div>
  );
}
